import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BrowserMultiFormatReader } from "@zxing/library";
import { Scan, Search, Trash2 } from "lucide-react";
import axios from "axios";
import { useNotification } from "../../hooks/useNotification";

const buildEmptyItem = () => ({
  id: Date.now() + Math.random(),
  quantity: 1,
  price: "",
  serialNumber: "",
  selectedVariant: "",
  variantSearch: "",
  showVariantDropdown: false,
  variantMeta: null,
});

const parsePriceInput = (value) => {
  const normalized = String(value ?? "")
    .replace(/,/g, ".")
    .replace(/[^\d.]/g, "")
    .trim();

  if (!normalized) return 0;

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getVariantDisplayName = (variant) => {
  if (!variant) return "";

  const modelCode = variant.product_model_code?.trim();
  const fallbackModelCode = variant.productModel?.product_model_code?.trim();
  const description = variant.product_model_description?.trim();

  return modelCode || fallbackModelCode || description || "";
};

const isBrokenModelCode = (variant) => {
  const modelCode = String(
    variant?.product_model_code || variant?.productModel?.product_model_code || "",
  ).trim();

  return !modelCode || modelCode === "000000";
};

const getVariantSearchLabel = (variant) => {
  if (!variant) return "";

  return getVariantDisplayName(variant);
};

const normalizeVariantSearchValue = (value) =>
  String(value || "").trim().toLowerCase();

const findExactVariantMatch = (variants, searchValue) => {
  const normalizedSearch = normalizeVariantSearchValue(searchValue);
  if (!normalizedSearch) return null;

  const exactMatches = (variants || []).filter(
    (variant) =>
      normalizeVariantSearchValue(getVariantSearchLabel(variant)) ===
      normalizedSearch,
  );

  return exactMatches.length === 1 ? exactMatches[0] : null;
};

const getColourPresentation = (colour) => {
  const normalizedColour = String(colour || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

  if (!normalizedColour) return null;

  const finishMap = [
    {
      match: ["metallic bronze", "bronze metallic"],
      swatchStyle: {
        background: "linear-gradient(135deg, #6f4e37 0%, #b87333 100%)",
        borderColor: "#8b5e3c",
      },
      textStyle: { color: "#8b5e3c" },
    },
    {
      match: ["black silver", "silver black"],
      swatchStyle: {
        background: "linear-gradient(135deg, #111827 0%, #9ca3af 100%)",
        borderColor: "#6b7280",
      },
      textStyle: { color: "#4b5563" },
    },
    {
      match: ["stainless steel"],
      swatchStyle: {
        background: "linear-gradient(135deg, #d1d5db 0%, #9ca3af 50%, #e5e7eb 100%)",
        borderColor: "#9ca3af",
      },
      textStyle: { color: "#6b7280" },
    },
    {
      match: ["matt black", "matte black"],
      swatchStyle: {
        backgroundColor: "#1f2937",
        borderColor: "#1f2937",
      },
      textStyle: { color: "#1f2937" },
    },
    {
      match: ["flat white"],
      swatchStyle: {
        backgroundColor: "#f9fafb",
        borderColor: "#d1d5db",
      },
      textStyle: { color: "#6b7280" },
    },
    {
      match: ["dark timber"],
      swatchStyle: {
        backgroundColor: "#6b4423",
        borderColor: "#6b4423",
      },
      textStyle: { color: "#6b4423" },
    },
    {
      match: ["light timber"],
      swatchStyle: {
        backgroundColor: "#c19a6b",
        borderColor: "#c19a6b",
      },
      textStyle: { color: "#a16207" },
    },
    {
      match: ["gun metal", "gunmetal"],
      swatchStyle: {
        backgroundColor: "#4b5563",
        borderColor: "#4b5563",
      },
      textStyle: { color: "#4b5563" },
    },
    {
      match: ["rose gold"],
      swatchStyle: {
        background: "linear-gradient(135deg, #b76e79 0%, #e0bfb8 100%)",
        borderColor: "#b76e79",
      },
      textStyle: { color: "#b76e79" },
    },
    {
      match: ["sand"],
      swatchStyle: {
        backgroundColor: "#d6c6a5",
        borderColor: "#d6c6a5",
      },
      textStyle: { color: "#a16207" },
      badgeStyle: {
        background: "#f5efe4",
        borderColor: "#d6c6a5",
        color: "#8a6a2f",
      },
    },
    {
      match: ["cream magnolia", "magnolia cream"],
      swatchStyle: {
        backgroundColor: "#f3e7cf",
        borderColor: "#e7d7b7",
      },
      textStyle: { color: "#8b7355" },
      badgeStyle: {
        background: "#fcf7ee",
        borderColor: "#e7d7b7",
        color: "#8b7355",
      },
    },
    {
      match: ["light pink", "soft pink", "baby pink"],
      swatchStyle: {
        backgroundColor: "#f7b6cb",
        borderColor: "#e88cb0",
      },
      textStyle: { color: "#a43e6b" },
      badgeStyle: {
        background: "#fff0f5",
        borderColor: "#e9a8c4",
        color: "#a43e6b",
      },
    },
  ];

  const matchedFinish = finishMap.find(({ match }) =>
    match.some((candidate) => normalizedColour.includes(candidate)),
  );

  if (matchedFinish) {
    return {
      ...matchedFinish,
      badgeStyle:
        matchedFinish.badgeStyle || {
          background: "#f8fafc",
          borderColor:
            matchedFinish.swatchStyle?.borderColor || "#d1d5db",
          color: matchedFinish.textStyle?.color || "#4b5563",
        },
    };
  }

  const tokenMap = {
    black: "#111827",
    white: "#ffffff",
    flat: "#ffffff",
    matt: "#111827",
    matte: "#111827",
    blue: "#2563eb",
    red: "#dc2626",
    green: "#16a34a",
    yellow: "#eab308",
    orange: "#f97316",
    purple: "#9333ea",
    pink: "#ec4899",
    brown: "#8b5e3c",
    bronze: "#b87333",
    cream: "#f3e7cf",
    magnolia: "#efe2c4",
    beige: "#e8d7b3",
    ivory: "#f8f4e8",
    light: "#d1d5db",
    grey: "#6b7280",
    gray: "#6b7280",
    silver: "#9ca3af",
    gold: "#d4a017",
    rose: "#b76e79",
    steel: "#94a3b8",
    stainless: "#cbd5e1",
    timber: "#8b5e3c",
    wood: "#8b5e3c",
    dark: "#374151",
    gun: "#4b5563",
    metal: "#6b7280",
    metallic: "#6b7280",
    sand: "#d6c6a5",
  };

  const tokens = normalizedColour.split(" ").filter(Boolean);
  const matchedColours = tokens
    .map((token) => tokenMap[token])
    .filter(Boolean);

  if (matchedColours.length === 0) {
    return {
      textStyle: { color: "#6b7280" },
      badgeStyle: {
        background: "#f8fafc",
        borderColor: "#d1d5db",
        color: "#6b7280",
      },
    };
  }

  const backgroundColor =
    matchedColours.length === 1
      ? matchedColours[0]
      : `linear-gradient(135deg, ${matchedColours.join(", ")})`;
  const primaryColour = matchedColours[0];
  const needsBorder =
    primaryColour === "#ffffff" ||
    primaryColour === "#f3e7cf" ||
    primaryColour === "#efe2c4" ||
    primaryColour === "#e8d7b3" ||
    primaryColour === "#d6c6a5" ||
    primaryColour === "#eab308";
  const textColor =
    primaryColour === "#ffffff"
      ? "#4b5563"
      : needsBorder
        ? "#8a6a2f"
        : primaryColour;

  return {
    swatchStyle: {
      backgroundColor,
      background: backgroundColor,
      borderColor: needsBorder ? "#d1d5db" : primaryColour,
    },
    textStyle: {
      color: textColor,
    },
    badgeStyle: {
      background: needsBorder ? "#fcfaf5" : "#f8fafc",
      borderColor: needsBorder ? "#e7d7b7" : `${primaryColour}33`,
      color: textColor,
    },
  };
};

const ColourMeta = ({ label, colour }) => {
  if (!colour) return null;

  const colourPresentation = getColourPresentation(colour);

  return (
    <span className="sale-colour-meta">
      {label ? <span className="sale-colour-meta-label">{label}</span> : null}
      <span
        className="sale-colour-pill"
        style={colourPresentation?.badgeStyle}
      >
        {colourPresentation?.swatchStyle ? (
          <span
            className="sale-colour-swatch"
            style={colourPresentation.swatchStyle}
            aria-hidden="true"
          />
        ) : null}
        <span style={colourPresentation?.textStyle}>{colour}</span>
      </span>
    </span>
  );
};

function SaleForm({
  mode = "sale",
  requireReceipt = false,
  onCancel,
  onSubmit,
  submitting,
  formError,
}) {
  const { t } = useTranslation();
  const { showWarning } = useNotification();
  const isReturnMode = mode === "return";
  const [formData, setFormData] = useState({
    items: [buildEmptyItem()],
    receiptNumber: "",
    receiptImage: null,
    notes: "",
  });

  const [receiptPreview, setReceiptPreview] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef(null);
  const codeReader = useRef(new BrowserMultiFormatReader());

  const [allVariants, setAllVariants] = useState({});
  const [loadingVariantsByItem, setLoadingVariantsByItem] = useState({});
  const searchTimeoutsRef = useRef({});
  const latestSearchRef = useRef({});

  const handleReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }

      setFormData((prev) => ({ ...prev, receiptImage: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveReceipt = () => {
    setFormData((prev) => ({ ...prev, receiptImage: null }));
    setReceiptPreview(null);
  };

  const stopBarcodeScan = useCallback(() => {
    setIsScanning(false);
    codeReader.current.reset();
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  const startBarcodeScan = useCallback(
    async (itemIndex) => {
      setIsScanning(itemIndex);
      try {
        const result = await codeReader.current.decodeOnceFromVideoDevice(
          undefined,
          videoRef.current,
        );
        if (result) {
          setFormData((prev) => {
            const newItems = [...prev.items];
            newItems[itemIndex].serialNumber = result.getText();
            return { ...prev, items: newItems };
          });
          stopBarcodeScan();
        }
      } catch (err) {
        console.error("Barcode scan failed:", err);
        stopBarcodeScan();
      }
    },
    [stopBarcodeScan],
  );

  useEffect(() => {
    if (isScanning !== false) {
      startBarcodeScan(isScanning);
    }
    return () => {
      if (isScanning !== false) {
        stopBarcodeScan();
      }
    };
  }, [isScanning, startBarcodeScan, stopBarcodeScan]);

  const fetchVariantOptions = useCallback(async (itemId, query = "") => {
    const normalizedQuery = String(query || "").trim();
    latestSearchRef.current[itemId] = normalizedQuery;

    setLoadingVariantsByItem((prev) => ({ ...prev, [itemId]: true }));

    try {
      const response = await axios.get("/api/product-hierarchy/variants", {
        params: {
          q: normalizedQuery || undefined,
          limit: 30,
        },
      });

      if (
        response.data.success &&
        latestSearchRef.current[itemId] === normalizedQuery
      ) {
        setAllVariants((prev) => ({
          ...prev,
          [itemId]: (response.data.data || []).filter(
            (variant) => !isBrokenModelCode(variant),
          ),
        }));
      }
    } catch (error) {
      console.error("Failed to load product options:", error);
      if (latestSearchRef.current[itemId] === normalizedQuery) {
        setAllVariants((prev) => ({
          ...prev,
          [itemId]: [],
        }));
      }
    } finally {
      if (latestSearchRef.current[itemId] === normalizedQuery) {
        setLoadingVariantsByItem((prev) => ({ ...prev, [itemId]: false }));
      }
    }
  }, []);

  const queueVariantFetch = useCallback(
    (itemId, query = "") => {
      if (searchTimeoutsRef.current[itemId]) {
        clearTimeout(searchTimeoutsRef.current[itemId]);
      }

      searchTimeoutsRef.current[itemId] = setTimeout(() => {
        fetchVariantOptions(itemId, query);
      }, 250);
    },
    [fetchVariantOptions],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".searchable-dropdown")) {
        setFormData((prev) => ({
          ...prev,
          items: prev.items.map((item) => ({
            ...item,
            showVariantDropdown: false,
          })),
        }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const currentTimeouts = searchTimeoutsRef.current;
    return () => {
      Object.values(currentTimeouts).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  const handleVariantSelect = (index, variant) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        selectedVariant: variant.product_id,
        variantSearch: getVariantSearchLabel(variant),
        showVariantDropdown: false,
        variantMeta: {
          categoryName:
            variant.productGroup?.product_group_description || "",
          productName: variant.product_description || "",
          modelName: getVariantDisplayName(variant),
          modelDescription: variant.product_model_description || "",
          colour: variant.colour || "",
          displayName: getVariantSearchLabel(variant),
          productId: variant.product_id,
        },
      };
      return { ...prev, items: newItems };
    });
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      const nextItem = { ...newItems[index], [field]: value };

      if (field === "variantSearch") {
        nextItem.showVariantDropdown = true;
        nextItem.selectedVariant = "";
        nextItem.variantMeta = null;
      }

      newItems[index] = nextItem;
      return { ...prev, items: newItems };
    });
  };

  const addItem = () => {
    if (formData.items.length < 30) {
      setFormData((prev) => ({
        ...prev,
        items: [...prev.items, buildEmptyItem()],
      }));
    }
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }));
    }
  };

  const grandTotal = useMemo(
    () =>
      formData.items.reduce((sum, item) => {
        const qty = Number.parseInt(item.quantity, 10) || 0;
        const price = parsePriceInput(item.price);
        return sum + qty * price;
      }, 0),
    [formData.items],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const invalidItemIndex = formData.items.findIndex(
      (item) =>
        !String(item.variantSearch || "").trim() ||
        !Number.isInteger(Number.parseInt(item.selectedVariant, 10)),
    );
    if (invalidItemIndex !== -1) {
      const message = t("se.saleForm.modelValidationError", {
        n: invalidItemIndex + 1,
      });
      showWarning(message);
      return;
    }

    if (requireReceipt && !formData.receiptImage) {
      const message = t("se.saleForm.receiptRequiredForReturn");
      showWarning(message);
      return;
    }

    const saleData = {
      items: formData.items.map((item) => ({
        product_id: item.selectedVariant
          ? Number.parseInt(item.selectedVariant, 10)
          : null,
        product_name:
          item.variantMeta?.productName || item.variantSearch.trim() || "",
        category_name: item.variantMeta?.categoryName || "",
        model_name: item.variantMeta?.modelName || item.variantSearch || "",
        variant_name: item.variantMeta?.displayName || item.variantSearch || "",
        quantity: Number.parseInt(item.quantity, 10) || 1,
        unit_price: parsePriceInput(item.price),
        serial_number: item.serialNumber || null,
      })),
      notes: formData.notes,
      receiptNumber: formData.receiptNumber,
      receiptFile: formData.receiptImage,
    };

    onSubmit(saleData);
  };

  return (
    <div className="sale-form-container sale-mobile-shell">
      <form id="sale-form" onSubmit={handleSubmit} className="sale-form">
        {formData.items.map((item, index) => {
          const filteredVariants = (allVariants[item.id] || []).filter(
            (variant) => !isBrokenModelCode(variant),
          );
          const isLoadingVariants = Boolean(loadingVariantsByItem[item.id]);

          return (
            <div key={item.id} className="sale-item-section sale-item-grid-section">
              <div className="sale-item-section-header">
                <h4>{t("se.saleForm.itemTitle", { n: index + 1 })}</h4>
                {formData.items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="sale-item-remove-btn"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    {t("se.saleForm.remove")}
                  </button>
                )}
              </div>

              <div className="form-group sale-entry-grid-model">
                <label className="sale-item-label sale-item-label-model">
                  {t("se.saleForm.modelLabel")}
                  <span className="required-asterisk" aria-hidden="true">*</span>
                </label>
                <div className="searchable-dropdown">
                  <div className="searchable-dropdown-input">
                    <Search size={18} className="searchable-dropdown-icon" />
                    <input
                      type="text"
                      value={item.variantSearch}
                      onChange={(e) => {
                        handleItemChange(index, "variantSearch", e.target.value);
                        queueVariantFetch(item.id, e.target.value);
                      }}
                      onFocus={() => {
                        handleItemChange(index, "showVariantDropdown", true);
                        queueVariantFetch(item.id, item.variantSearch);
                      }}
                      onBlur={() => {
                        const exactMatch = findExactVariantMatch(
                          filteredVariants,
                          item.variantSearch,
                        );

                        if (exactMatch) {
                          handleVariantSelect(index, exactMatch);
                        }
                      }}
                      onKeyDown={(e) => {
                        const exactMatch = findExactVariantMatch(
                          filteredVariants,
                          item.variantSearch,
                        );

                        if (e.key === "Enter") {
                          e.preventDefault();

                          if (exactMatch) {
                            handleVariantSelect(index, exactMatch);
                          } else if (filteredVariants.length === 1) {
                            handleVariantSelect(index, filteredVariants[0]);
                          }
                        } else if (e.key === "Escape") {
                          handleItemChange(index, "showVariantDropdown", false);
                        }
                      }}
                      placeholder={
                        isLoadingVariants
                          ? t("se.saleForm.loadingModels")
                          : t("se.saleForm.searchModelQuick")
                      }
                      className="searchable-dropdown-text-input"
                    />
                  </div>
                  {item.showVariantDropdown && (
                    <div className="dropdown-list">
                      {isLoadingVariants ? (
                        <div className="dropdown-item disabled">
                          {t("se.saleForm.loadingModels")}
                        </div>
                      ) : filteredVariants.length > 0 ? (
                        filteredVariants.slice(0, 30).map((variant) => (
                          <div
                            key={variant.product_id}
                            className="dropdown-item"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              handleVariantSelect(index, variant);
                            }}
                          >
                            <div className="sale-model-option-name">
                              {getVariantSearchLabel(variant)}
                            </div>
                            <div className="sale-model-option-meta">
                              <ColourMeta
                                label={`${t("se.saleForm.colourHelperLabel")} `}
                                colour={variant.colour}
                              />
                              {variant.colour && variant.product_model_description
                                ? " · "
                                : ""}
                              {variant.product_model_description || ""}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item disabled">
                          {t("se.saleForm.noModelsFound")}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {item.variantMeta && (
                  <div className="sale-model-meta">
                    <div>
                      {item.variantMeta.colour ? (
                        <ColourMeta
                          label={`${t("se.saleForm.colourHelperLabel")} `}
                          colour={item.variantMeta.colour}
                        />
                      ) : (
                        <>
                          <strong>{t("se.saleForm.colourHelperLabel")}</strong>{" "}
                          {t("se.saleForm.notAvailable")}
                        </>
                      )}
                    </div>
                    <div>
                      <strong>{t("se.saleForm.modelDescriptionHelperLabel")}</strong>{" "}
                      {item.variantMeta.modelDescription ||
                        t("se.saleForm.notAvailable")}
                    </div>
                  </div>
                )}
              </div>

              <div className="sale-entry-inline-row">
                <div className="form-group sale-entry-grid-qty">
                  <label>
                    {t("se.saleForm.quantityLabel")}
                    <span className="required-asterisk" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(index, "quantity", e.target.value)
                    }
                    placeholder="1"
                    required
                  />
                  {isReturnMode && (
                    <small className="quantity-hint">
                      {t("se.saleForm.returnQuantityHint")}
                    </small>
                  )}
                </div>

                <div className="form-group sale-entry-grid-price">
                  <label>
                    {t("se.saleForm.unitPriceLabel")}
                    <span className="required-asterisk" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={item.price}
                    onChange={(e) =>
                      handleItemChange(index, "price", e.target.value)
                    }
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t("se.saleForm.serialLabel")}</label>
                <div className="serial-input-container">
                  <input
                    type="text"
                    value={item.serialNumber}
                    onChange={(e) =>
                      handleItemChange(index, "serialNumber", e.target.value)
                    }
                    placeholder={t("se.saleForm.serialPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => setIsScanning(index)}
                    className="scan-btn"
                    title="Scan barcode"
                  >
                    <Scan size={20} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {formData.items.length < 30 && (
          <button
            type="button"
            onClick={addItem}
            className="sale-add-item-btn"
          >
            <span style={{ fontSize: "18px" }}>+</span>{" "}
            {t("se.saleForm.addAnotherItem")}
          </button>
        )}

        <div className="sale-receipt-card">
          <h3 className="sale-receipt-card-title">
            {t("se.saleForm.receiptSectionTitle")}
          </h3>

          <div className="form-group">
            <div className="receipt-upload-container">
              {!receiptPreview ? (
                <label className="receipt-upload-label">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleReceiptUpload}
                    style={{ display: "none" }}
                  />
                  <div className="receipt-upload-box sale-receipt-upload-box">
                    <span>{t("se.saleForm.clickToUpload")}</span>
                    <small>{t("se.saleForm.uploadHint")}</small>
                  </div>
                </label>
              ) : (
                <div className="receipt-preview">
                  <img src={receiptPreview} alt="Receipt preview" />
                  <button
                    type="button"
                    onClick={handleRemoveReceipt}
                    className="receipt-remove-btn"
                  >
                    {t("se.saleForm.removeReceipt")}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>{t("se.saleForm.receiptNumberLabel")}</label>
            <input
              type="text"
              value={formData.receiptNumber}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, receiptNumber: e.target.value }))
              }
              placeholder={t("se.saleForm.receiptNumberPlaceholder")}
            />
          </div>

          <div className="form-group">
            <label>{t("se.saleForm.notesLabel")}</label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder={t("se.saleForm.notesPlaceholder")}
              rows="2"
            />
          </div>
        </div>

        {formError && <div className="form-error">{formError}</div>}
      </form>

      <div className="sale-mobile-footer">
        <div
          className={`sale-mobile-footer-total ${
            isReturnMode ? "return-mode" : "sale-mode"
          }`}
        >
          <span>{t("se.saleForm.batchTotalLabel")}</span>
          <strong>RM {grandTotal.toFixed(2)}</strong>
        </div>
        <div className="sale-mobile-footer-actions">
          <button type="button" onClick={onCancel} className="btn-cancel sale-mobile-cancel">
            {t("se.saleForm.cancel")}
          </button>
          <button type="submit" form="sale-form" className="btn-submit sale-mobile-submit" disabled={submitting}>
            {submitting
              ? t("se.saleForm.saving")
              : isReturnMode
                ? t("se.saleForm.saveReturnSale")
                : t("se.saleForm.saveSale")}
          </button>
        </div>
      </div>

      {isScanning !== false && (
        <div className="barcode-scan-overlay">
          <div className="barcode-scan-modal">
            <h3>{t("se.saleForm.scanBarcode")}</h3>
            <video ref={videoRef} className="barcode-video" />
            <div className="scan-actions">
              <button onClick={stopBarcodeScan} className="btn-cancel">
                {t("se.saleForm.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SaleForm;
